import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CloseOutlined,
  FileExclamationFilled,
  FilePdfFilled,
  FileWordFilled,
  FilterOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import {
  setFilterChoice,
  setSortingChoice,
} from "../../redux/slices/folderSlice.ts";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { Button, Tooltip } from "antd";
import { gray, green, orange } from "../../utils/theme/colors.ts";

interface Option {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface Props {
  type: "filter" | "sort";
  isMyFoldersView?: boolean;
  includeSpecialFilters?: boolean;
}

const filterOptions: Option[] = [
  { value: "all", label: "Activos", icon: <FilePdfFilled /> },
  {
    value: "to_review",
    label: "Activos con documentos por revisar",
    icon: <FilePdfFilled style={{ color: orange }} />,
  },
  {
    value: "pending",
    label: "Sin documentos por recibir",
    icon: <FilePdfFilled style={{ color: green }} />,
  },
  {
    value: "disabled",
    label: "Desactivados",
    icon: <FileExclamationFilled style={{ color: gray }} />,
  },
  {
    value: "multireport_delivered",
    label: "Con multireportes entregados",
    icon: <FilePdfFilled style={{ color: green }} />,
  },
  {
    value: "multi_report_delivered_no_contract",
    label: "Multireporte entregado, sin contrato",
    icon: <FileWordFilled style={{ color: orange }} />,
  },
  {
    value: "is_ready_for_multireport",
    label: "Listos Multireporte",
    icon: <FilePdfFilled style={{ color: green }} />,
  },
];

const specialFilters: Option[] = [
  {
    value: "filter_folders_with_oldest_pending_review",
    label: "Mesa de Control",
    icon: <FileWordFilled style={{ color: orange }} />,
  },
  {
    value: "cja_validated_filter",
    label: "Contrato",
    icon: <FileWordFilled style={{ color: green }} />,
  },
];

const sortOptions: Option[] = [
  { value: "recent", label: "Recientes", icon: <SortDescendingOutlined /> },
  { value: "oldest", label: "Mas antiguos", icon: <SortAscendingOutlined /> },
  {
    value: "recent_loaded",
    label: "Cargas recientes",
    icon: <ReloadOutlined />,
  },
];

export const FilterSortDropdown: React.FC<Props> = ({
  type,
  isMyFoldersView,
  includeSpecialFilters,
}) => {
  const [activeOption, setActiveOption] = useState<string | null>(null);
  const [activeSort, setActiveSort] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const allFilterOptions = includeSpecialFilters
    ? specialFilters
    : filterOptions;

  const filterValue = allFilterOptions.find(
    (opt) => opt.value === activeOption,
  );

  const sortValue = sortOptions.find((opt) => opt.value === activeSort);
  const { userInfo } = useAppSelector((state) => state.auth);
  const isOperations = userInfo?.groups.includes("operations");
  const isCja = userInfo?.groups.includes("cja");
  const escListenerRef = useRef<(e: KeyboardEvent) => void>(undefined);

  const dispatch = useAppDispatch();

  const options = type === "filter" ? allFilterOptions : sortOptions;
  const icon =
    type === "filter" ? <FilterOutlined /> : <SortAscendingOutlined />;

  const setDefaultFilterSort = useCallback(() => {
    if (isMyFoldersView && isOperations) {
      sessionStorage.setItem(
        "specialFilter",
        "filter_folders_with_oldest_pending_review",
      );
    } else if (isMyFoldersView && isCja) {
      sessionStorage.setItem("specialFilter", "cja_validated_filter");
    } else {
      sessionStorage.setItem("folderFilter", "all");
      sessionStorage.setItem("folderSort", "default");
      sessionStorage.setItem("specialFilter", "");
    }
  }, [isOperations, isMyFoldersView]);

  useEffect(() => {
    escListenerRef.current = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisible(false);
    };
    document.addEventListener("keydown", escListenerRef.current);
    return () => {
      if (escListenerRef.current) {
        document.removeEventListener("keydown", escListenerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setDefaultFilterSort();

    const savedFilter = sessionStorage.getItem("folderFilter") || "all";
    const savedSpecialFilter = sessionStorage.getItem("specialFilter") || "";
    const savedSort = sessionStorage.getItem("folderSort") || "default";

    if (type === "filter") {
      if (includeSpecialFilters) {
        if (savedSpecialFilter && (isMyFoldersView || hasInteracted)) {
          setActiveOption(savedSpecialFilter);
          setHasInteracted(true);
          dispatch(setFilterChoice(savedSpecialFilter));
        }
      } else {
        setActiveOption(savedFilter);
        dispatch(setFilterChoice(savedFilter));
      }
    } else {
      setActiveSort(savedSort !== "default" ? savedSort : null);
      dispatch(setSortingChoice(savedSort));
    }
  }, [type, setDefaultFilterSort, dispatch, includeSpecialFilters]);

  const handleSelect = useCallback(
    (value: string) => {
      setHasInteracted(true);

      if (type === "filter") {
        if (includeSpecialFilters) {
          if (activeOption === value) {
            setActiveOption(null);
            sessionStorage.setItem("specialFilter", "");
            dispatch(setFilterChoice(""));
          } else {
            setActiveOption(value);
            sessionStorage.setItem("specialFilter", value);
            dispatch(setFilterChoice(value));
          }
        } else {
          setActiveOption(value);
          sessionStorage.setItem("folderFilter", value);
          dispatch(setFilterChoice(value));
        }
      } else {
        if (activeSort === value) {
          setActiveSort(null);
          sessionStorage.setItem("folderSort", "default");
          dispatch(setSortingChoice("default"));
        } else {
          setActiveSort(value);
          sessionStorage.setItem("folderSort", value);
          dispatch(setSortingChoice(value));
        }
      }
      setVisible(false);
    },
    [
      type,
      activeSort,
      activeOption,
      dispatch,
      includeSpecialFilters,
      setHasInteracted,
    ],
  );

  const onClearFilterOrSort = useCallback(
    (clearType: string) => {
      if (clearType === "filter") {
        if (includeSpecialFilters) {
          setActiveOption(null);
          sessionStorage.setItem("specialFilter", "");
          dispatch(setFilterChoice(""));
        } else {
          dispatch(setFilterChoice("all"));
          sessionStorage.setItem("folderFilter", "all");
          setActiveOption(null);
        }
      } else {
        dispatch(setSortingChoice("default"));
        sessionStorage.setItem("folderSort", "default");
        setActiveSort(null);
      }
      setVisible(false);
    },
    [dispatch, includeSpecialFilters],
  );

  return (
    <div className="container" style={{ fontSize: "0.925rem" }}>
      <div className="relative">
        <Button
          onClick={() => setVisible(!visible)}
          icon={icon}
          className={`flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 transition-opacity duration-800 ease-in-out hover:bg-gray-200`}
        >
          <span className="text-sm max-w-[100px] truncate block">
            {type === "filter"
              ? (filterValue?.label ??
                (includeSpecialFilters ? "Filtros Especiales" : "Filtros"))
              : (sortValue?.label ?? "Ordenar por")}
          </span>
          {((type === "filter" && activeOption) ||
            (type === "sort" && sortValue)) && (
            <Tooltip
              title={type === "filter" ? "Quitar filtro" : "Quitar orden"}
            >
              <div
                className="ml-auto cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearFilterOrSort(type);
                }}
              >
                <CloseOutlined className="text-xs" />
              </div>
            </Tooltip>
          )}
        </Button>
        {visible && (
          <div className="absolute left-0 mt-2 w-64 p-4 bg-white rounded-xl shadow-lg z-50">
            <h3 className="text-sm text-gray-700 mb-4">
              Selecciona una opción
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {options.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleSelect(filter.value)}
                  className={`flex flex-col items-center justify-center h-24 rounded-lg transition ${
                    type === "filter"
                      ? activeOption === filter.value
                        ? includeSpecialFilters
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "bg-gray-100 text-gray-700"
                        : "bg-white text-gray-700"
                      : activeSort === filter.value
                        ? "bg-gray-100 text-gray-700"
                        : "bg-white text-gray-700"
                  }`}
                >
                  {filter.icon}
                  <span className="mt-2 text-sm">{filter.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
